import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { useAuth } from '@/lib/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { formatINR, formatCarats } from '@/lib/format';
import { Plus, Search, Gem, Eye, Pencil, Trash2, ArrowRightLeft, PackageX, FileText, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Textarea } from '@/components/ui/textarea';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import { VendorFormDialog } from '@/components/phase10/VendorFormDialog';
import { ClassificationFormDialog } from '@/components/phase10/ClassificationFormDialog';
import { ClassificationEditDialog } from '@/components/phase10/ClassificationEditDialog';
import PacketTransferDialog from '@/components/phase17/PacketTransferDialog';
import { ImageUploadInput } from '@/components/ImageUploadInput';
import { ImageThumb } from '@/components/ImageThumb';
import { BrokenPacketCreateDialog } from '@/components/broken-packets/BrokenPacketCreateDialog';
import { BrokenBadge } from '@/components/broken-packets/BrokenBadge';
import { ImageListPdfDialog } from '@/components/reports/ImageListPdfDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';

export default function Pearls() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell title="Pearls" subtitle="Classifications, packets and ledgers for loose pearl inventory (Akoya, South Sea, Freshwater…)">
      <CompanyGate>
        <PearlsContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function PearlsContent({ companyId }) {
  const [tab, setTab] = useState('packets');
  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList className="bg-white border border-[var(--divider)]">
        <TabsTrigger value="packets" data-testid="pearl-tab-packets">Packets</TabsTrigger>
        <TabsTrigger value="classifications" data-testid="pearl-tab-classifications">Classifications</TabsTrigger>
        <TabsTrigger value="closing" data-testid="pearl-tab-closing">Closing Balance</TabsTrigger>
      </TabsList>
      <TabsContent value="packets" className="mt-5"><PacketsTab companyId={companyId} /></TabsContent>
      <TabsContent value="classifications" className="mt-5"><ClassificationsTab companyId={companyId} /></TabsContent>
      <TabsContent value="closing" className="mt-5"><ClosingTab companyId={companyId} /></TabsContent>
    </Tabs>
  );
}

function ClassificationsTab({ companyId }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  // Wave 3.48 Fix #1 — guard against double-click submitting a duplicate.
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ schema_code: '', shape_name: '', description: '', tag_display_name: '' });
  const [delTarget, setDelTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get('/pearls/classifications', { params: { company_id: companyId } });
    setList(data);
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const create = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post('/pearls/classifications', { ...form, schema_code: form.schema_code.toUpperCase(), company_id: companyId });
      toast.success('Classification created');
      setOpen(false); setForm({ schema_code: '', shape_name: '', description: '', tag_display_name: '' }); load();
    } catch (e) { toast.error(extractError(e, 'Create failed')); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Pearl Classifications</div>
          <div className="text-xs text-slate-500">Create a 2–15 character code (e.g. D-RB, D-PR). Used as prefix for packet auto-numbering.</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="luxury-button" data-testid="classifications-add-button"><Plus size={14} className="mr-1.5" /> New Classification</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Pearl Classification</DialogTitle>
              <DialogDescription>Create a shape category used as a prefix for packet numbers.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Schema code (2–15 chars, A–Z 0–9 ‐)</Label>
                <Input data-testid="classification-schema-input" placeholder="e.g. D-RB" value={form.schema_code}
                  onChange={(e) => setForm({ ...form, schema_code: e.target.value.toUpperCase() })} maxLength={15} />
              </div>
              <div>
                <Label>Shape name</Label>
                <Input data-testid="classification-shape-input" placeholder="e.g. Round Brilliant" value={form.shape_name}
                  onChange={(e) => setForm({ ...form, shape_name: e.target.value })} />
              </div>
              <div>
                <Label>Tag Display Name (optional)</Label>
                <Input data-testid="classification-tagname-input" placeholder="Printed on jewellery tags instead of the code" value={form.tag_display_name}
                  onChange={(e) => setForm({ ...form, tag_display_name: e.target.value })} />
                <p className="text-[11px] text-slate-500 mt-1">Printed on jewellery tags exactly as typed. Leave blank to print the classification name.</p>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea data-testid="classification-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={create} className="luxury-button" data-testid="classification-submit-button" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="overflow-hidden">
        <div className="ledger-table">
          <table className="w-full">
            <thead><tr>
              <th className="text-left">Schema</th>
              <th className="text-left">Shape</th>
              <th className="text-left">Tag Display Name</th>
              <th className="text-right">Packets</th>
              <th className="text-left">Description</th>
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody data-testid="classifications-table-body">
              {list.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No classifications yet</td></tr>}
              {list.map((c) => (
                <tr key={c.id} data-testid={`classification-row-${c.id}`}>
                  <td><span className="font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded">{c.schema_code}</span></td>
                  <td>{c.shape_name}</td>
                  <td className="text-[13px]" data-testid={`classification-tagname-${c.id}`}>{c.tag_display_name ? c.tag_display_name : <span className="text-slate-400">—</span>}</td>
                  <td className="text-right font-mono tabular-nums">{c.packet_count}</td>
                  <td className="text-slate-500 text-xs">{c.description}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => setEditTarget(c)} className="text-xs text-[var(--ink-navy)] hover:underline mr-3" data-testid={`classification-edit-${c.id}`}><Pencil size={12} className="inline mr-1" />Edit</button>
                    <button onClick={() => setDelTarget(c)} className="text-xs text-red-600 hover:underline" data-testid={`classification-delete-${c.id}`}><Trash2 size={12} className="inline mr-1" />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {delTarget && (
        <DeleteConfirmDialog
          open={!!delTarget}
          onOpenChange={(v) => { if (!v) setDelTarget(null); }}
          entity_type="pearl_classification"
          entity_id={delTarget.id}
          entity_label={`${delTarget.schema_code} — ${delTarget.shape_name}`}
          company_id={companyId}
          onSuccess={() => { setDelTarget(null); load(); }}
        />
      )}
      <ClassificationEditDialog
        open={!!editTarget}
        onOpenChange={(v) => { if (!v) setEditTarget(null); }}
        companyId={companyId}
        material="pearl"
        classification={editTarget}
        onUpdated={() => { setEditTarget(null); load(); }}
      />
    </div>
  );
}

function PacketsTab({ companyId }) {
  // Wave 3.41 — Restricted users hide Rate / Value columns entirely.
  const { canViewValues } = useAuth();
  const [list, setList] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState({ classification_id: '', search: '' });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    classification_id: '', valuation_mode: 'fixed',
    initial_carats: 0, initial_pieces: 0, initial_rate_per_ct: 0,
    vendor_id: '', vendor_name: '', description: '',
    purchase_status: 'purchased',
    credit_days: '',  // Wave 3.36 — only sent when purchase_status='purchased'
    image_data_url: '',  // Wave 3.37 — optional packet image
  });
  // Phase 10 — inline master creation
  const [classDlgOpen, setClassDlgOpen] = useState(false);
  // Wave 3 — Packet Transfer
  const [transferOpen, setTransferOpen] = useState(false);
  const [vendorDlgOpen, setVendorDlgOpen] = useState(false);
  // Phase 13.5 — Empty packet toggle (persisted)
  const SHOW_EMPTY_KEY = 'inventique.pearls.showEmptyPackets';
  const [showEmpty, setShowEmpty] = useState(() => {
    try { return localStorage.getItem(SHOW_EMPTY_KEY) === '1'; }
    catch { return false; }
  });
  const persistShowEmpty = (v) => {
    setShowEmpty(v);
    try { localStorage.setItem(SHOW_EMPTY_KEY, v ? '1' : '0'); } catch (e) { /* ignore */ }
  };
  // Wave 3.45 Phase 2 — Broken Packet primitive
  const [brokenList, setBrokenList] = useState([]);
  const [brokenDlgOpen, setBrokenDlgOpen] = useState(false);
  const [showBroken, setShowBroken] = useState(false);
  // Wave 3.48 Fix #1 — guard against double-click submission.
  const [submitting, setSubmitting] = useState(false);

  const isEmpty = (p) => (Number(p.balance_carats || 0) <= 0.0005) && (Number(p.balance_pieces || 0) <= 0);
  const visible = showEmpty ? list : list.filter((p) => !isEmpty(p));
  const purchasedVisible = visible.filter((p) => p.purchase_status !== 'on_approval');
  const onApprovalVisible = visible.filter((p) => p.purchase_status === 'on_approval');
  const hiddenCount = list.length - visible.length;

  const load = async () => {
    const params = { company_id: companyId };
    if (filter.classification_id) params.classification_id = filter.classification_id;
    if (filter.search) params.search = filter.search;
    const { data } = await api.get('/pearls/packets', { params });
    setList(data);
    if (showBroken) {
      const { data: bdata } = await api.get('/pearls/packets', { params: { ...params, packet_type: 'broken' } });
      setBrokenList(bdata);
    } else {
      setBrokenList([]);
    }
  };
  const loadClassifications = async () => {
    const { data } = await api.get('/pearls/classifications', { params: { company_id: companyId } });
    setClassifications(data);
  };
  const loadVendors = async () => {
    // Vendors master is gated by ``vendors.view`` — a material-scoped
    // operator may lack it. Swallow a 403 so the packet list (loaded
    // independently) is never blocked; the vendor picker simply shows no
    // options for such users.
    try {
      const { data } = await api.get('/masters/vendors', { params: { company_id: companyId } });
      setVendors(Array.isArray(data) ? data : []);
    } catch { setVendors([]); }
  };
  useEffect(() => { if (companyId) { load(); loadClassifications(); loadVendors(); } }, [companyId]);
  useEffect(() => { if (companyId) load(); /* eslint-disable-next-line */ }, [filter]);
  // Wave 3.45 Phase 2 — reload list when broken toggle changes
  useEffect(() => { if (companyId) load(); /* eslint-disable-next-line */ }, [showBroken]);

  const create = async () => {
    if (submitting) return;          // Wave 3.48 Fix #1 — debounce
    setSubmitting(true);
    try {
      const payload = {
        ...form, company_id: companyId,
        initial_carats: Number(form.initial_carats) || 0,
        initial_pieces: Number(form.initial_pieces) || 0,
        initial_rate_per_ct: Number(form.initial_rate_per_ct) || 0,
      };
      const v = vendors.find(v => v.id === form.vendor_id);
      if (v) payload.vendor_name = v.name;
      // Wave 3.36 — only send credit_days for purchased openings with vendor.
      if (form.purchase_status === 'purchased' && form.vendor_id && form.credit_days !== '' && form.credit_days !== null) {
        const n = Number(form.credit_days);
        payload.credit_days = Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
      } else {
        payload.credit_days = null;
      }
      // Wave 3.37 — optional image.
      payload.image_data_url = form.image_data_url || null;
      const { data } = await api.post('/pearls/packets', payload);
      toast.success(`Packet ${data.packet_no} created`);
      setOpen(false);
      setForm({ classification_id: '', valuation_mode: 'fixed', initial_carats: 0, initial_pieces: 0, initial_rate_per_ct: 0, vendor_id: '', vendor_name: '', description: '', purchase_status: 'purchased', credit_days: '', image_data_url: '' });
      load();
    } catch (e) { toast.error(extractError(e, 'Create failed')); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <div className="sticky-filter-bar flex flex-col md:flex-row items-start md:items-end justify-between gap-3 mb-3 rounded-lg px-3" data-testid="pearls-filter-bar">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Pearl Packets</div>
          <div className="text-xs text-slate-500">Auto-numbered packets by classification; choose fixed or running-average valuation.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex items-center gap-2 mr-2 self-center mt-4">
            <Switch
              id="show-empty-packets"
              checked={showEmpty}
              onCheckedChange={persistShowEmpty}
              data-testid="pearl-show-empty-toggle"
            />
            <Label
              htmlFor="show-empty-packets"
              className="text-xs text-slate-600 cursor-pointer select-none"
              data-testid="pearl-show-empty-label"
            >
              Show empty packets
              {hiddenCount > 0 && !showEmpty && (
                <span className="ml-1 text-[10px] text-slate-400">({hiddenCount} hidden)</span>
              )}
            </Label>
          </div>
          {/* Wave 3.45 Phase 2 — Show Broken toggle */}
          <div className="flex items-center gap-2 mr-2 self-center mt-4">
            <Switch
              id="pearl-show-broken-packets"
              checked={showBroken}
              onCheckedChange={setShowBroken}
              data-testid="pearl-show-broken-toggle"
            />
            <Label
              htmlFor="pearl-show-broken-packets"
              className="text-xs text-rose-700 cursor-pointer select-none flex items-center gap-1"
              data-testid="pearl-show-broken-label"
            >
              <PackageX size={11} /> Show broken
            </Label>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Classification</Label>
            <div className="w-44">
              <SearchableSelect
                items={[{ id: '__all__', schema_code: '', shape_name: 'All classifications' }, ...classifications]}
                value={filter.classification_id || '__all__'}
                onValueChange={(v) => setFilter({ ...filter, classification_id: !v || v === '__all__' ? '' : v })}
                itemToLabel={(c) => (c.id === '__all__' ? 'All classifications' : `${c.schema_code} · ${c.shape_name}`)}
                searchKeys={['schema_code', 'shape_name']}
                placeholder="All classifications"
                searchPlaceholder="Search shape / code…"
                allowClear={false}
                testId="packets-filter-classification"
              />
            </div>
          </div>
          <div className="relative">
            <Label className="text-[11px] text-slate-500">Search</Label>
            <Search size={12} className="absolute left-3 bottom-2.5 text-slate-400" />
            <Input className="pl-7 h-9 w-44" placeholder="Packet no…" value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })} data-testid="packets-search-input" />
          </div>
          <Button variant="outline" className="h-9" onClick={() => setTransferOpen(true)} data-testid="packets-transfer-button">
            <ArrowRightLeft size={14} className="mr-1.5" /> Transfer
          </Button>
          {/* Wave 3.45 Phase 2 — Broken (salvage) packet creation. */}
          <Button
            variant="outline"
            className="h-9 border-rose-200 text-rose-700 hover:bg-rose-50"
            onClick={() => setBrokenDlgOpen(true)}
            data-testid="packets-new-broken-button"
          >
            <PackageX size={14} className="mr-1.5" /> Broken Packet
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="luxury-button h-9" data-testid="packets-add-button"><Plus size={14} className="mr-1.5" /> New Packet</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Pearl Packet</DialogTitle>
                <DialogDescription>Pick a classification — packet number is auto-assigned.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Classification</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SearchableSelect
                        items={classifications}
                        value={form.classification_id}
                        onValueChange={(v) => setForm({ ...form, classification_id: v })}
                        itemToLabel={(c) => `${c.schema_code} — ${c.shape_name}`}
                        searchKeys={['schema_code', 'shape_name']}
                        placeholder={classifications.length === 0 ? 'No classifications — create one →' : 'Select shape…'}
                        searchPlaceholder="Search shape / code…"
                        disabled={classifications.length === 0}
                        testId="packet-classification-select"
                      />
                    </div>
                    <Button type="button" variant="outline" size="icon" onClick={() => setClassDlgOpen(true)} data-testid="packet-classification-create-button" title="Create classification">
                      <Plus size={14} />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Valuation mode</Label>
                  <Select value={form.valuation_mode} onValueChange={(v) => setForm({ ...form, valuation_mode: v })}>
                    <SelectTrigger data-testid="packet-valuation-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Rate</SelectItem>
                      <SelectItem value="running_avg">Running Average</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-slate-500 mt-1">{form.valuation_mode === 'fixed' ? 'Rate stays constant unless you edit it' : 'Rate recalculates on every receipt'}</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Opening carats</Label>
                    <Input type="number" step="0.01" value={form.initial_carats} data-testid="packet-initial-carats-input"
                      onChange={(e) => setForm({ ...form, initial_carats: e.target.value })} />
                  </div>
                  <div>
                    <Label>Opening pieces</Label>
                    <Input type="number" step="1" value={form.initial_pieces} data-testid="packet-initial-pieces-input"
                      onChange={(e) => setForm({ ...form, initial_pieces: e.target.value })} />
                  </div>
                  <div>
                    <Label>Rate ₹/ct</Label>
                    <Input type="number" step="1" value={form.initial_rate_per_ct} data-testid="packet-initial-rate-input"
                      onChange={(e) => setForm({ ...form, initial_rate_per_ct: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Vendor (optional)</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SearchableSelect
                        items={vendors}
                        value={form.vendor_id || ''}
                        onValueChange={(v) => {
                          const vid = v || '';
                          const pickedVendor = vendors.find((vv) => vv.id === vid);
                          setForm((prev) => ({
                            ...prev,
                            vendor_id: vid,
                            credit_days: prev.credit_days !== '' && prev.credit_days !== null
                              ? prev.credit_days
                              : (pickedVendor && pickedVendor.default_credit_days != null
                                  ? String(pickedVendor.default_credit_days)
                                  : ''),
                          }));
                        }}
                        itemToLabel={(v) => v.name}
                        itemToSubLabel={(v) => [v.phone, v.email].filter(Boolean).join(' · ') || null}
                        searchKeys={['name', 'phone', 'email']}
                        placeholder="None"
                        searchPlaceholder="Search vendor name / phone / email…"
                        allowClear
                        testId="packet-vendor-select"
                      />
                    </div>
                    <Button type="button" variant="outline" size="icon" onClick={() => setVendorDlgOpen(true)} data-testid="packet-vendor-create-button" title="Create vendor">
                      <Plus size={14} />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Purchase Status</Label>
                  <Select value={form.purchase_status} onValueChange={(v) => setForm({ ...form, purchase_status: v })}>
                    <SelectTrigger data-testid="packet-purchase-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="purchased">Stock (Purchased) — posts to vendor payable</SelectItem>
                      <SelectItem value="on_approval">On Approval — value held until conversion</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {form.purchase_status === 'on_approval'
                      ? 'Vendor amount ledger will hold this packet under "On Approval" until you convert it to stock.'
                      : 'Vendor amount ledger receives the packet value immediately as payable.'}
                  </div>
                </div>
                {/* Wave 3.36 — Credit days for purchased openings only. */}
                {form.purchase_status === 'purchased' && form.vendor_id && (
                  <div>
                    <Label>Credit Days (optional)</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      placeholder="e.g. 30"
                      value={form.credit_days}
                      onChange={(e) => setForm({ ...form, credit_days: e.target.value })}
                      data-testid="packet-credit-days-input"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">
                      Due date = opening date + credit days. Prefilled from vendor default ({vendors.find((v) => v.id === form.vendor_id)?.default_credit_days ?? '—'}) when set. Leave blank for no due date.
                    </div>
                  </div>
                )}
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="packet-description-input" />
                </div>
                {form.classification_id && form.initial_carats > 0 && (
                  <Card className="p-3 bg-[var(--paper-warm)]">
                    <div className="text-xs text-slate-500">Opening valuation preview</div>
                    <div className="font-mono text-sm text-[var(--ink-navy)]">
                      {formatCarats(form.initial_carats)} ct × ₹{Number(form.initial_rate_per_ct).toLocaleString('en-IN')}/ct = {formatINR(Number(form.initial_carats) * Number(form.initial_rate_per_ct))}
                    </div>
                  </Card>
                )}
                {/* Wave 3.37 — Optional image of the packet (max 2 MB). */}
                <ImageUploadInput
                  label="Packet Image (optional)"
                  value={form.image_data_url}
                  onChange={(v) => setForm((p) => ({ ...p, image_data_url: v }))}
                  helpText="Max 2 MB. Shown as a thumbnail in the packet list."
                  testIdPrefix="packet-image"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} className="luxury-button" data-testid="packet-submit-button" disabled={submitting || !form.classification_id}>
                  {submitting ? 'Creating…' : 'Create Packet'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Card className="overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-center gap-2">
          <span className="inline-flex w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-sm font-medium text-[var(--ink-navy)]">Purchased Stock</span>
          <Badge variant="secondary" className="text-[10px]" data-testid="packets-purchased-count">{purchasedVisible.length}</Badge>
        </div>
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              {/* Wave 3.37 — image thumbnail column */}
              <th className="text-left w-14">Image</th>
              <th className="text-left">Packet No</th>
              <th className="text-left">Shape</th>
              <th className="text-left">Mode</th>
              <th className="text-right">Carats</th>
              <th className="text-right">Pieces</th>
              <th className="text-right">Avg Pkt Size</th>
              {/* Wave 3.41 — Rate / Value hidden for users without values.view */}
              {canViewValues && <th className="text-right">Rate (₹/ct)</th>}
              {canViewValues && <th className="text-right">Value</th>}
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody data-testid="packets-table-body-purchased">
              {purchasedVisible.length === 0 && <tr><td colSpan={canViewValues ? 10 : 8} className="text-center py-8 text-slate-500 text-sm">{showEmpty ? 'No purchased packets yet.' : 'No purchased packets with balance. Toggle "Show empty packets" to view all.'}</td></tr>}
              {purchasedVisible.map((p) => {
                const empty = isEmpty(p);
                const emptyCellClass = empty ? 'text-rose-600 font-semibold' : '';
                return (
                <tr key={p.id} data-testid={`packet-row-${p.id}`} className={empty ? 'bg-rose-50/30' : ''}>
                  {/* Wave 3.37 — thumbnail */}
                  <td className="py-1.5">
                    <ImageThumb src={p.image} alt={p.packet_no} size={40} title={`${p.packet_no} image`} testId={`packet-image-${p.id}`} />
                  </td>
                  <td>
                    <Link
                      to={`/pearls/packets/${p.id}`}
                      className="font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded hover:underline hover:bg-[var(--diamond-bg)]/80 transition-colors"
                      data-testid={`packet-no-link-${p.id}`}
                    >{p.packet_no}</Link>
                    {empty && (
                      <Badge className="ml-2 bg-rose-100 text-rose-700 border-rose-200 text-[9px]" data-testid={`packet-empty-badge-${p.id}`}>EMPTY</Badge>
                    )}
                  </td>
                  <td>
                    <div>{p.classification?.shape_name || ''}</div>
                  </td>
                  <td><Badge variant="secondary" className="text-[10px]">{p.valuation_mode === 'fixed' ? 'Fixed' : 'Run. Avg'}</Badge></td>
                  <td className={`text-right font-mono tabular-nums ${emptyCellClass}`}>{formatCarats(p.balance_carats)}</td>
                  <td className={`text-right font-mono tabular-nums ${emptyCellClass}`}>{p.balance_pieces}</td>
                  <td className="text-right font-mono tabular-nums" data-testid={`packet-avg-size-${p.id}`} title="Average carats per piece">{formatCarats(p.avg_size_ct || 0)}</td>
                  {canViewValues && <td className="text-right font-mono tabular-nums">{(p.current_rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>}
                  {canViewValues && <td className={`text-right font-mono tabular-nums ${emptyCellClass}`}>{formatINR(p.balance_value)}</td>}
                  <td className="text-right">
                    <Link to={`/pearls/packets/${p.id}`} className="inline-flex items-center text-xs text-[var(--ink-navy)] hover:underline" data-testid={`packet-open-${p.id}`}>
                      <Eye size={12} className="mr-1" /> Open
                    </Link>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden border-amber-200">
        <div className="px-4 pt-3 pb-2 flex items-center gap-2 bg-amber-50/40">
          <span className="inline-flex w-2 h-2 rounded-full bg-amber-500" />
          <span className="text-sm font-medium text-amber-900">Packets on Approval</span>
          <Badge className="bg-amber-100 text-amber-800 text-[10px] border-amber-200" data-testid="packets-on-approval-count">{onApprovalVisible.length}</Badge>
          <span className="text-[11px] text-amber-700 ml-2">Value sits in the vendor&apos;s On-Approval bucket until you convert it to Stock.</span>
        </div>
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              {/* Wave 3.37 — image thumbnail column */}
              <th className="text-left w-14">Image</th>
              <th className="text-left">Packet No</th>
              <th className="text-left">Shape</th>
              <th className="text-left">Vendor</th>
              <th className="text-left">Mode</th>
              <th className="text-right">Carats</th>
              <th className="text-right">Pieces</th>
              <th className="text-right">Avg Pkt Size</th>
              {canViewValues && <th className="text-right">Rate (₹/ct)</th>}
              {canViewValues && <th className="text-right">Value</th>}
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody data-testid="packets-table-body-on-approval">
              {onApprovalVisible.length === 0 && <tr><td colSpan={canViewValues ? 11 : 9} className="text-center py-8 text-slate-500 text-sm">{showEmpty ? 'No packets on approval.' : 'No on-approval packets with balance.'}</td></tr>}
              {onApprovalVisible.map((p) => {
                const empty = isEmpty(p);
                const emptyCellClass = empty ? 'text-rose-600 font-semibold' : '';
                return (
                <tr key={p.id} data-testid={`packet-row-${p.id}`} className={empty ? 'bg-rose-50/30' : 'bg-amber-50/20'}>
                  {/* Wave 3.37 — thumbnail */}
                  <td className="py-1.5">
                    <ImageThumb src={p.image} alt={p.packet_no} size={40} title={`${p.packet_no} image`} testId={`packet-image-${p.id}`} />
                  </td>
                  <td>
                    <Link
                      to={`/pearls/packets/${p.id}`}
                      className="font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded hover:underline hover:bg-[var(--diamond-bg)]/80 transition-colors"
                      data-testid={`packet-no-link-${p.id}`}
                    >{p.packet_no}</Link>
                    {empty && (
                      <Badge className="ml-2 bg-rose-100 text-rose-700 border-rose-200 text-[9px]" data-testid={`packet-empty-badge-${p.id}`}>EMPTY</Badge>
                    )}
                  </td>
                  <td>
                    <div>{p.classification?.shape_name || ''}</div>
                    <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[9px] mt-0.5" data-testid={`packet-on-approval-${p.id}`}>On Approval</Badge>
                  </td>
                  <td className="text-[12px] text-slate-700" data-testid={`packet-vendor-${p.id}`}>{p.vendor_name || '—'}</td>
                  <td><Badge variant="secondary" className="text-[10px]">{p.valuation_mode === 'fixed' ? 'Fixed' : 'Run. Avg'}</Badge></td>
                  <td className={`text-right font-mono tabular-nums ${emptyCellClass}`}>{formatCarats(p.balance_carats)}</td>
                  <td className={`text-right font-mono tabular-nums ${emptyCellClass}`}>{p.balance_pieces}</td>
                  <td className="text-right font-mono tabular-nums" data-testid={`packet-avg-size-${p.id}`} title="Average carats per piece">{formatCarats(p.avg_size_ct || 0)}</td>
                  {canViewValues && <td className="text-right font-mono tabular-nums">{(p.current_rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>}
                  {canViewValues && <td className={`text-right font-mono tabular-nums ${emptyCellClass}`}>{formatINR(p.balance_value)}</td>}
                  <td className="text-right">
                    <Link to={`/pearls/packets/${p.id}`} className="inline-flex items-center text-xs text-[var(--ink-navy)] hover:underline" data-testid={`packet-open-${p.id}`}>
                      <Eye size={12} className="mr-1" /> Open
                    </Link>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Wave 3.45 Phase 2 — Broken (salvage) Packets section. */}
      {showBroken && (
        <Card className="overflow-hidden border-rose-200" data-testid="broken-packets-section">
          <div className="px-4 pt-3 pb-2 flex items-center gap-2 bg-rose-50/40">
            <PackageX size={14} className="text-rose-700" />
            <div className="font-medium text-rose-900">Broken Packets</div>
            <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] ml-1">
              {brokenList.length}
            </Badge>
            <div className="text-[11px] text-rose-700/70 ml-2">
              Salvage containers — cannot be issued, transferred or sold.
            </div>
          </div>
          <div className="px-4 pb-4 pt-1 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[12px] text-slate-500">
                <tr>
                  <th className="text-left w-14">Image</th>
                  <th className="text-left">Packet No</th>
                  <th className="text-left">Shape</th>
                  <th className="text-left">Description</th>
                  <th className="text-right">Carats</th>
                  <th className="text-right">Pieces</th>
                  {canViewValues && <th className="text-right">Value</th>}
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="broken-packets-tbody">
                {brokenList.length === 0 && (
                  <tr>
                    <td colSpan={canViewValues ? 8 : 7} className="text-center py-6 text-slate-500 text-sm">
                      No broken packets yet. Click <span className="font-medium text-rose-700">+ Broken Packet</span> above to create one.
                    </td>
                  </tr>
                )}
                {brokenList.map((p) => (
                  <tr key={p.id} data-testid={`broken-packet-row-${p.id}`} className="bg-rose-50/30">
                    <td className="py-1.5">
                      <ImageThumb src={p.image} alt={p.packet_no} size={40} title={p.packet_no} testId={`broken-image-${p.id}`} />
                    </td>
                    <td>
                      <Link
                        to={`/pearls/packets/${p.id}`}
                        className="font-mono text-[12px] bg-rose-100 text-rose-800 px-2 py-0.5 rounded hover:underline"
                        data-testid={`broken-no-link-${p.id}`}
                      >
                        {p.packet_no}
                      </Link>
                      <BrokenBadge size="sm" testId={`broken-badge-${p.id}`} />
                    </td>
                    <td>{p.classification?.shape_name || ''}</td>
                    <td className="text-[12px] text-slate-700">{p.description || '—'}</td>
                    <td className="text-right font-mono tabular-nums">{formatCarats(p.balance_carats)}</td>
                    <td className="text-right font-mono tabular-nums">{p.balance_pieces}</td>
                    {canViewValues && (
                      <td className="text-right font-mono tabular-nums">{formatINR(p.balance_value)}</td>
                    )}
                    <td className="text-right">
                      <Link to={`/pearls/packets/${p.id}`} className="inline-flex items-center text-xs text-[var(--ink-navy)] hover:underline" data-testid={`broken-open-${p.id}`}>
                        <Eye size={12} className="mr-1" /> Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Phase 10 — inline classification + vendor creation dialogs */}
      <ClassificationFormDialog
        open={classDlgOpen}
        onOpenChange={setClassDlgOpen}
        companyId={companyId}
        material="pearl"
        onCreated={async (cls) => { await loadClassifications(); setForm((f) => ({ ...f, classification_id: cls.id })); }}
      />
      <VendorFormDialog
        open={vendorDlgOpen}
        onOpenChange={setVendorDlgOpen}
        companyId={companyId}
        onCreated={async (v) => { await loadVendors(); setForm((f) => ({ ...f, vendor_id: v.id, vendor_name: v.name })); }}
      />
      {/* Wave 3.45 Phase 2 — Broken Packet creation dialog. */}
      <BrokenPacketCreateDialog
        open={brokenDlgOpen}
        onOpenChange={setBrokenDlgOpen}
        material="pearl"
        companyId={companyId}
        defaultClassificationId={filter.classification_id}
        onCreated={() => { setShowBroken(true); load(); }}
      />
    </div>
  );
}

function ClosingTab({ companyId }) {
  // Wave 3.41 — Restricted users hide Rate / Value columns entirely.
  const { canViewValues } = useAuth();
  const [data, setData] = useState(null);
  // Wave 3.17.4 — visibility filters
  const [showEmpty, setShowEmpty] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | stock | approval
  // Wave 3.55 — Image List PDF dialog
  const [imgListOpen, setImgListOpen] = useState(false);
  // Wave 3.60 — PDF export options (values + branding).
  const [includeValues, setIncludeValues] = useState(() => {
    try { return localStorage.getItem('inventique.pearls.pdfIncludeValues') !== '0'; }
    catch (e) { return true; }
  });
  const [includeBranding, setIncludeBranding] = useState(() => {
    try { return localStorage.getItem('inventique.pearls.pdfIncludeBranding') !== '0'; }
    catch (e) { return true; }
  });
  const setPdfIncludeValues = (v) => {
    setIncludeValues(v);
    try { localStorage.setItem('inventique.pearls.pdfIncludeValues', v ? '1' : '0'); } catch (e) { /* ignore */ }
  };
  const setPdfIncludeBranding = (v) => {
    setIncludeBranding(v);
    try { localStorage.setItem('inventique.pearls.pdfIncludeBranding', v ? '1' : '0'); } catch (e) { /* ignore */ }
  };
  const load = async () => {
    const { data } = await api.get('/pearls/closing-balance', { params: { company_id: companyId } });
    setData(data);
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);

  // Wave 3.57 — same detail/summary variant selection as Diamonds; the
  // ``view`` param is ignored server-side for Excel exports.
  // Wave 3.60 — forwards ``include_values`` + ``include_branding`` for PDFs.
  const dl = async (kind, variant = 'detailed') => {
    try {
      const params = { company_id: companyId };
      if (kind === 'pdf') {
        params.view = variant;
        params.include_values = includeValues ? 'true' : 'false';
        params.include_branding = includeBranding ? 'true' : 'false';
      }
      const res = await api.get(`/reports/closing-balance/pearls/${kind}`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      const suffix = kind === 'pdf' && variant === 'summary' ? '_summary' : '';
      const flag = kind === 'pdf' ? `${includeValues ? '' : '_novalue'}${includeBranding ? '' : '_nobranding'}` : '';
      a.download = `pearl_closing_balance${suffix}${flag}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${kind.toUpperCase()}${kind === 'pdf' ? ` — ${variant === 'summary' ? 'Summary' : 'Detailed'}` : ''}`);
    } catch (e) { toast.error('Download failed'); }
  };

  const visibleTree = React.useMemo(() => {
    if (!data) return [];
    const includePacket = (p) => {
      const hasBal = (Number(p.balance_carats) || 0) > 0 || (Number(p.balance_pieces) || 0) > 0;
      if (!showEmpty && !hasBal) return false;
      const isApproval = (p.purchase_status || 'purchased') === 'on_approval';
      if (statusFilter === 'stock' && isApproval) return false;
      if (statusFilter === 'approval' && !isApproval) return false;
      return true;
    };
    return (data.tree || [])
      .map((node) => {
        const packets = (node.packets || []).filter(includePacket);
        const sub_ct = packets.reduce((s, p) => s + (Number(p.balance_carats) || 0), 0);
        const sub_pcs = packets.reduce((s, p) => s + (Number(p.balance_pieces) || 0), 0);
        const sub_val = packets.reduce((s, p) => s + (Number(p.balance_value) || 0), 0);
        const sub_avg = sub_pcs > 0 ? sub_ct / sub_pcs : 0;
        return { ...node, packets, subtotal: { carats: sub_ct, pieces: sub_pcs, value: sub_val, avg_size_ct: sub_avg, packet_count: packets.length } };
      })
      .filter((node) => node.packets.length > 0);
  }, [data, showEmpty, statusFilter]);

  const grandFiltered = React.useMemo(() => {
    const grand_ct = visibleTree.reduce((s, n) => s + (n.subtotal.carats || 0), 0);
    const grand_pcs = visibleTree.reduce((s, n) => s + (n.subtotal.pieces || 0), 0);
    const grand_val = visibleTree.reduce((s, n) => s + (n.subtotal.value || 0), 0);
    return { carats: grand_ct, pieces: grand_pcs, value: grand_val, avg_size_ct: grand_pcs > 0 ? grand_ct / grand_pcs : 0 };
  }, [visibleTree]);

  if (!data) return <div className="text-slate-500 py-6">Loading…</div>;
  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Closing Balance — Pearls</div>
          <div className="text-xs text-slate-500">Tree: classification → packet with subtotals & grand total.</div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="pearl-closing-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Goods</SelectItem>
              <SelectItem value="stock">Stock Only</SelectItem>
              <SelectItem value="approval">Approval Only</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
            <Switch checked={showEmpty} onCheckedChange={setShowEmpty} id="pearl-closing-show-empty" data-testid="pearl-closing-show-empty-toggle" />
            <span>Show empty packets</span>
          </label>
          {/* Wave 3.57 — Export PDF dropdown mirrors Diamonds behaviour. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="closing-export-pdf">
                <FileText size={14} className="mr-1.5" />
                Export PDF
                <ChevronDown size={14} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
                Options
              </DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={includeValues}
                onCheckedChange={setPdfIncludeValues}
                onSelect={(e) => e.preventDefault()}
                data-testid="closing-export-pdf-opt-values"
              >
                Include Value (Rate + Total ₹)
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={includeBranding}
                onCheckedChange={setPdfIncludeBranding}
                onSelect={(e) => e.preventDefault()}
                data-testid="closing-export-pdf-opt-branding"
              >
                Include Company Branding
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
                Choose report format
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => dl('pdf', 'detailed')}
                data-testid="closing-export-pdf-detailed"
                className="flex-col items-start gap-0.5"
              >
                <span className="text-sm text-[var(--ink-navy)]">Detailed (with images)</span>
                <span className="text-[11px] text-slate-500">Every packet on its own row with thumbnail</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => dl('pdf', 'summary')}
                data-testid="closing-export-pdf-summary"
                className="flex-col items-start gap-0.5"
              >
                <span className="text-sm text-[var(--ink-navy)]">Summary (collapsed)</span>
                <span className="text-[11px] text-slate-500">One row per classification / vendor, totals only</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => dl('excel')} data-testid="closing-export-excel">Export Excel</Button>
          <Button
            variant="outline"
            onClick={() => setImgListOpen(true)}
            data-testid="closing-image-list-pdf-button"
            title="Catalogue-style PDF with packet images"
          >
            Image List PDF
          </Button>
        </div>
      </div>
      <ImageListPdfDialog
        open={imgListOpen}
        onOpenChange={setImgListOpen}
        companyId={companyId}
        scope="pearl"
        moduleLabel="Pearls"
        defaultFilename="image_list_pearls.pdf"
      />
      <Card className="overflow-hidden">
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              {/* Wave 3.37 — image thumbnail column */}
              <th className="text-left w-14">Image</th>
              <th className="text-left">Packet</th>
              <th className="text-left">Mode</th>
              <th className="text-left">Stock or Approval Goods</th>
              {/* Wave 3.41 — Rate hidden for users without values.view */}
              {canViewValues && <th className="text-right">Rate (₹/ct)</th>}
              <th className="text-right">Carats</th>
              <th className="text-right">Pieces</th>
              <th className="text-right">Avg Pkt Size</th>
              {canViewValues && <th className="text-right">Value</th>}
            </tr></thead>
            <tbody>
              {visibleTree.length === 0 && <tr><td colSpan={canViewValues ? 9 : 7} className="text-center py-10 text-slate-500">No packets match the current filters.</td></tr>}
              {visibleTree.map((node) => (
                <React.Fragment key={node.classification.id}>
                  <tr className="bg-[var(--pearl-bg,#F1F5F9)]">
                    <td colSpan={canViewValues ? 9 : 7} className="font-medium text-[var(--pearl-icon,#475569)]">
                      {node.classification.schema_code} — {node.classification.shape_name} ({node.packets.length} packets)
                    </td>
                  </tr>
                  {node.packets.map((p) => {
                    const isApproval = (p.purchase_status || 'purchased') === 'on_approval';
                    return (
                      <tr key={p.id} data-testid={`pearl-closing-row-${p.id}`}>
                        {/* Wave 3.37 — thumbnail */}
                        <td className="py-1.5">
                          <ImageThumb src={p.image} alt={p.packet_no} size={36} title={`${p.packet_no} image`} testId={`pearl-closing-image-${p.id}`} />
                        </td>
                        <td className="font-mono">
                          <Link
                            to={`/pearls/packets/${p.id}`}
                            className="text-[var(--ink-navy)] hover:underline hover:text-[var(--diamond-icon)]"
                            data-testid={`closing-packet-no-link-${p.id}`}
                          >{p.packet_no}</Link>
                        </td>
                        <td>{p.valuation_mode === 'fixed' ? 'Fixed' : 'Run. Avg'}</td>
                        <td>
                          <Badge className={`text-[10px] border ${isApproval ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-testid={`pearl-closing-row-${p.id}-status`}>
                            {isApproval ? 'Approval' : 'Stock'}
                          </Badge>
                        </td>
                        {canViewValues && <td className="text-right font-mono tabular-nums">{(p.current_rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>}
                        <td className="text-right font-mono tabular-nums">{formatCarats(p.balance_carats)}</td>
                        <td className="text-right font-mono tabular-nums">{p.balance_pieces}</td>
                        <td className="text-right font-mono tabular-nums" title="Average carats per piece">{formatCarats(p.avg_size_ct || 0)}</td>
                        {canViewValues && <td className="text-right font-mono tabular-nums">{formatINR(p.balance_value)}</td>}
                      </tr>
                    );
                  })}
                  <tr className="bg-[var(--goldsmith-bg)] font-medium">
                    <td colSpan={canViewValues ? 5 : 4} className="text-right">Subtotal</td>
                    <td className="text-right font-mono tabular-nums">{formatCarats(node.subtotal.carats)}</td>
                    <td className="text-right font-mono tabular-nums">{node.subtotal.pieces}</td>
                    <td className="text-right font-mono tabular-nums" title="Avg carats per piece across this classification">{formatCarats(node.subtotal.avg_size_ct || 0)}</td>
                    {canViewValues && <td className="text-right font-mono tabular-nums">{formatINR(node.subtotal.value)}</td>}
                  </tr>
                </React.Fragment>
              ))}
              {visibleTree.length > 0 && (
                <tr className="bg-[var(--ink-navy)] text-white font-medium">
                  <td colSpan={canViewValues ? 5 : 4} className="text-right text-white">Grand Total</td>
                  <td className="text-right font-mono tabular-nums">{formatCarats(grandFiltered.carats)}</td>
                  <td className="text-right font-mono tabular-nums">{grandFiltered.pieces}</td>
                  <td className="text-right font-mono tabular-nums" title="Avg carats per piece across all classifications">{formatCarats(grandFiltered.avg_size_ct || 0)}</td>
                  {canViewValues && <td className="text-right font-mono tabular-nums">{formatINR(grandFiltered.value)}</td>}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
